import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Plus, Trash2, Check, Users, RefreshCw, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StudioBonusAccountingCard } from '@/components/settings/StudioBonusAccountingCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const CHF = (n) => new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(n || 0);

const STAFF_ROLES = ['admin', 'trainer', 'staff', 'superadmin', 'mieter'];

export default function StudioBonusSettingsPage({ embedded = false }) {
  const navigate = useNavigate();
  const [studios, setStudios] = useState(null);
  const [staff, setStaff] = useState([]);
  const [previews, setPreviews] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadPreview = useCallback(async (studioId) => {
    try {
      const r = await axios.get(`${API}/studio-bonus/studio/${studioId}`, authHeader());
      setPreviews((p) => ({ ...p, [studioId]: r.data }));
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [sRes, uRes] = await Promise.all([
        axios.get(`${API}/studio-bonus/settings`, authHeader()),
        axios.get(`${API}/users`, authHeader()),
      ]);
      const list = sRes.data.studios || [];
      setStudios(list);
      const staffList = (uRes.data || []).filter((u) => STAFF_ROLES.includes((u.role || '').toLowerCase()));
      setStaff(staffList);
      list.forEach((s) => loadPreview(s.studio_id));
    } catch (e) {
      toast.error('Bonus-Einstellungen konnten nicht geladen werden');
    }
  }, [loadPreview]);

  useEffect(() => { load(); }, [load]);

  const patchStudio = (idx, patch) => {
    setStudios((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const setTier = (idx, ti, field, value) => {
    setStudios((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const tiers = [...(s.tiers || [])];
      tiers[ti] = { ...tiers[ti], [field]: value };
      return { ...s, tiers };
    }));
  };

  const addTier = (idx) => setStudios((prev) => prev.map((s, i) => (
    i === idx ? { ...s, tiers: [...(s.tiers || []), { min_revenue: 0, percent: 0 }] } : s
  )));

  const removeTier = (idx, ti) => setStudios((prev) => prev.map((s, i) => (
    i === idx ? { ...s, tiers: (s.tiers || []).filter((_, j) => j !== ti) } : s
  )));

  const addManager = (idx, userId) => {
    if (!userId) return;
    setStudios((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      if ((s.managers || []).some((m) => m.user_id === userId)) return s;
      return { ...s, managers: [...(s.managers || []), { user_id: userId, role: 'gf', share_percent: 100 }] };
    }));
  };

  const setManager = (idx, mi, field, value) => setStudios((prev) => prev.map((s, i) => {
    if (i !== idx) return s;
    const managers = [...(s.managers || [])];
    managers[mi] = { ...managers[mi], [field]: value };
    return { ...s, managers };
  }));

  const removeManager = (idx, mi) => setStudios((prev) => prev.map((s, i) => (
    i === idx ? { ...s, managers: (s.managers || []).filter((_, j) => j !== mi) } : s
  )));

  const staffName = (id) => {
    const u = staff.find((x) => x.id === id);
    return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : id;
  };

  const save = async (idx) => {
    const s = studios[idx];
    // 100%-Validierung (10/2026): warnen, wenn die GF-Anteile zusammen nicht
    // 100% ergeben — verhindert falsche Aufteilungen ans ERP (z.B. 60+30).
    const mgrs = s.managers || [];
    if (s.enabled && mgrs.length > 0) {
      const sum = mgrs.reduce((acc, m) => acc + (Number(m.share_percent) || 0), 0);
      if (Math.round(sum * 100) / 100 !== 100) {
        const proceed = window.confirm(
          `⚠️ Achtung: Die GF-Anteile für ${s.studio_name} ergeben zusammen ${sum}%, nicht 100%.\n\n` +
          `Dadurch wird der Standort-Bonus nicht vollständig (oder doppelt) aufgeteilt und genau so ans ERP/die Lohnbuchhaltung gesendet.\n\n` +
          `Trotzdem speichern?`
        );
        if (!proceed) return;
      }
    }
    setSavingId(s.studio_id);
    try {
      const body = {
        enabled: !!s.enabled,
        mode: s.mode === 'fixed' ? 'fixed' : 'tiers',
        fixed_percent: Number(s.fixed_percent) || 0,
        revenue_per_customer: Number(s.revenue_per_customer) || 260,
        tiers: (s.tiers || []).map((t) => ({
          min_revenue: Number(t.min_revenue) || 0,
          percent: Number(t.percent) || 0,
        })),
        managers: (s.managers || []).map((m) => ({
          user_id: m.user_id,
          role: m.role === 'stv' ? 'stv' : 'gf',
          share_percent: Number(m.share_percent) || 0,
        })),
      };
      await axios.put(`${API}/studio-bonus/settings/${s.studio_id}`, body, authHeader());
      toast.success(`Bonus für ${s.studio_name} gespeichert`);
      loadPreview(s.studio_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  if (!studios) return <div className="p-8 text-muted-foreground">Lädt…</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="studio-bonus-page">
      <div className="flex items-center gap-3">
        {!embedded && (
          <Button variant="ghost" size="icon" data-testid="studio-bonus-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">Standort-Bonus (Geschäftsführung)</h1>
            <p className="text-sm text-muted-foreground">
              Umsatzabhängiger Bonus je Studio · Bonus = % × Monatsumsatz (bezahlte Rechnungen)
            </p>
          </div>
        </div>
      </div>

      {studios.length === 0 && (
        <Card><CardContent className="py-8 text-center text-muted-foreground">Keine aktiven Studios gefunden.</CardContent></Card>
      )}

      <StudioBonusAccountingCard />

      {studios.map((s, idx) => {
        const preview = previews[s.studio_id];
        const isFixed = s.mode === 'fixed';
        const rpc = Number(s.revenue_per_customer) || 260;
        const availableStaff = staff.filter((u) => !(s.managers || []).some((m) => m.user_id === u.id));
        return (
          <Card key={s.studio_id} className="border-amber-200" data-testid={`studio-bonus-card-${s.studio_id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-amber-600" /> {s.studio_name}
                    <Badge className={`text-[10px] ${s.enabled ? 'bg-amber-600' : 'bg-gray-400'}`}>
                      {s.enabled ? 'Aktiv' : 'Aus'}
                    </Badge>
                  </CardTitle>
                  <CardDescription>Konfiguration des GF-Bonus für dieses Studio.</CardDescription>
                </div>
                <Switch
                  checked={!!s.enabled}
                  onCheckedChange={(v) => patchStudio(idx, { enabled: !!v })}
                  data-testid={`studio-bonus-enabled-${s.studio_id}`}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Live-Vorschau */}
              {preview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-lg font-bold" data-testid={`studio-bonus-preview-revenue-${s.studio_id}`}>{CHF(preview.revenue)}</p>
                    <p className="text-xs text-gray-600">Umsatz (Monat) · ≈ {preview.approx_customers} Kunden</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-amber-700">{preview.percent}%</p>
                    <p className="text-xs text-gray-600">aktueller Bonus-Satz</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-amber-700" data-testid={`studio-bonus-preview-chf-${s.studio_id}`}>{CHF(preview.bonus_chf)}</p>
                    <p className="text-xs text-gray-600">Bonus (Studio)</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    {preview.next_tier ? (
                      <>
                        <p className="text-sm font-semibold">+{CHF(preview.next_tier.revenue_to_go)}</p>
                        <p className="text-xs text-gray-600">bis {preview.next_tier.percent}% (≈ {preview.next_tier.approx_customers} Kunden)</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-green-700">Max.</p>
                        <p className="text-xs text-gray-600">Höchste Stufe</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Modus */}
              <div className="space-y-2">
                <Label>Bonus-Modus</Label>
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid={`studio-bonus-mode-${s.studio_id}`}>
                  <button type="button" onClick={() => patchStudio(idx, { mode: 'tiers' })}
                    className={`px-4 py-1.5 text-sm font-medium ${!isFixed ? 'bg-amber-600 text-white' : 'bg-white text-gray-600'}`}
                    data-testid={`studio-bonus-mode-tiers-${s.studio_id}`}>Stufen (Umsatz)</button>
                  <button type="button" onClick={() => patchStudio(idx, { mode: 'fixed' })}
                    className={`px-4 py-1.5 text-sm font-medium ${isFixed ? 'bg-amber-600 text-white' : 'bg-white text-gray-600'}`}
                    data-testid={`studio-bonus-mode-fixed-${s.studio_id}`}>Fix %</button>
                </div>
              </div>

              {isFixed ? (
                <div className="space-y-1 max-w-xs">
                  <Label>Fixer Bonus-Satz (% vom Umsatz)</Label>
                  <Input type="number" step="0.5" min="0" max="100"
                    data-testid={`studio-bonus-fixed-${s.studio_id}`}
                    value={s.fixed_percent ?? 0}
                    onChange={(e) => patchStudio(idx, { fixed_percent: e.target.value })} />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label>Stufen: ab Monatsumsatz (CHF) → Bonus %</Label>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">Ø Umsatz/Kunde</span>
                      <Input type="number" step="10" min="1" className="w-24 h-8"
                        data-testid={`studio-bonus-rpc-${s.studio_id}`}
                        value={s.revenue_per_customer ?? 260}
                        onChange={(e) => patchStudio(idx, { revenue_per_customer: e.target.value })} />
                      <span className="text-xs text-gray-500">CHF</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    Massgebend ist der Umsatz. Die Kundenzahl ist nur ein Indikator (Umsatz ÷ Ø Umsatz/Kunde).
                  </p>
                  <div className="space-y-2">
                    {(s.tiers || []).map((t, ti) => (
                      <div key={ti} className="flex items-center gap-2 flex-wrap" data-testid={`studio-bonus-tier-${s.studio_id}-${ti}`}>
                        <span className="text-xs text-gray-500 w-6">ab</span>
                        <Input type="number" step="100" min="0" className="w-36"
                          data-testid={`studio-bonus-tier-rev-${s.studio_id}-${ti}`}
                          value={t.min_revenue}
                          onChange={(e) => setTier(idx, ti, 'min_revenue', e.target.value)} />
                        <span className="text-xs text-gray-500">CHF →</span>
                        <Input type="number" step="0.5" min="0" max="100" className="w-20"
                          data-testid={`studio-bonus-tier-pct-${s.studio_id}-${ti}`}
                          value={t.percent}
                          onChange={(e) => setTier(idx, ti, 'percent', e.target.value)} />
                        <span className="text-xs text-gray-500">%</span>
                        <span className="text-[11px] text-gray-400 min-w-[70px]" data-testid={`studio-bonus-tier-kunden-${s.studio_id}-${ti}`}>
                          ≈ {Math.round((Number(t.min_revenue) || 0) / rpc)} Kunden
                        </span>
                        <Button variant="ghost" size="icon" className="text-red-500 h-8 w-8"
                          data-testid={`studio-bonus-tier-remove-${s.studio_id}-${ti}`}
                          onClick={() => removeTier(idx, ti)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => addTier(idx)}
                    data-testid={`studio-bonus-tier-add-${s.studio_id}`}>
                    <Plus className="w-4 h-4 mr-1" /> Stufe hinzufügen
                  </Button>
                </div>
              )}

              {/* Manager */}
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Geschäftsführer / Stv.-GF (nur diese erhalten den Bonus)</Label>
                <div className="space-y-2">
                  {(s.managers || []).map((m, mi) => (
                    <div key={m.user_id} className="flex items-center gap-2 flex-wrap" data-testid={`studio-bonus-mgr-${s.studio_id}-${m.user_id}`}>
                      <span className="text-sm font-medium min-w-[160px]">{staffName(m.user_id)}</span>
                      <Select value={m.role || 'gf'} onValueChange={(v) => setManager(idx, mi, 'role', v)}>
                        <SelectTrigger className="w-36 h-9" data-testid={`studio-bonus-mgr-role-${s.studio_id}-${m.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gf">Geschäftsführer</SelectItem>
                          <SelectItem value="stv">Stv. Geschäftsführer</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Input type="number" step="1" min="0" max="100" className="w-20 h-9"
                          data-testid={`studio-bonus-mgr-share-${s.studio_id}-${m.user_id}`}
                          value={m.share_percent ?? 100}
                          onChange={(e) => setManager(idx, mi, 'share_percent', e.target.value)} />
                        <span className="text-xs text-gray-500">% Anteil</span>
                      </div>
                      {preview && (
                        <Badge variant="outline" className="text-[11px]">
                          ≈ {CHF((preview.bonus_chf || 0) * (Number(m.share_percent) || 0) / 100)}
                        </Badge>
                      )}
                      <Button variant="ghost" size="icon" className="text-red-500 h-8 w-8"
                        data-testid={`studio-bonus-mgr-remove-${s.studio_id}-${m.user_id}`}
                        onClick={() => removeManager(idx, mi)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(s.managers || []).length === 0 && (
                    <p className="text-xs text-gray-400">Noch keine Geschäftsführer zugewiesen — niemand sieht den Bonus.</p>
                  )}
                  {(s.managers || []).length > 0 && (() => {
                    const sum = (s.managers || []).reduce((a, m) => a + (Number(m.share_percent) || 0), 0);
                    const ok = Math.round(sum * 100) / 100 === 100;
                    return (
                      <p className={`text-xs font-medium ${ok ? 'text-green-600' : 'text-amber-600'}`}
                         data-testid={`studio-bonus-share-total-${s.studio_id}`}>
                        {ok ? '✓' : '⚠️'} Summe der Anteile: {sum}%{ok ? '' : ' (sollte 100% ergeben)'}
                      </p>
                    );
                  })()}
                </div>
                <div className="max-w-xs">
                  <Select value="" onValueChange={(v) => addManager(idx, v)}>
                    <SelectTrigger className="h-9" data-testid={`studio-bonus-mgr-add-${s.studio_id}`}>
                      <SelectValue placeholder="+ Mitarbeiter als GF hinzufügen" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStaff.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} · {u.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={() => save(idx)} disabled={savingId === s.studio_id}
                  className="bg-amber-600 hover:bg-amber-700" data-testid={`studio-bonus-save-${s.studio_id}`}>
                  <Check className="w-4 h-4 mr-2" />
                  {savingId === s.studio_id ? 'Speichert…' : 'Speichern'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => loadPreview(s.studio_id)}
                  data-testid={`studio-bonus-refresh-${s.studio_id}`}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Vorschau aktualisieren
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
